import { Table, TableBody, TableCell, TableContainer, TableRow, Grid } from '@mui/material';
import { ODSText } from '@telekom-ods/react-ui-kit';
import React, { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { getTheme } from '../../helpers/theme';
import useAppContext from '../../hooks/useAppContext';
import Alert from '../Alert';
import Button from '../Buttons/Button';
import IntroHeader from '../Table/IntroHeader';
import Tooltip from '../Tooltip';

import type { Row, TranslatableLabel } from '@/types/tableTypes';

interface Action {
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
  label: string;
}

interface ExtraAction extends Action {
  rightIcon?: boolean;
  leftIcon?: boolean;
  icon?: ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
}

interface OverviewTableProps {
  header?: string;
  subHeader?: string;
  rows?: Row[];
  editAction?: Action;
  deleteAction?: Action;
  extraActions?: ExtraAction[];
  trailingActionSlot?: React.ReactNode;
}

const OverviewTable = ({
  header = '',
  subHeader = '',
  rows = [],
  editAction,
  deleteAction,
  extraActions = [],
  trailingActionSlot,
}: OverviewTableProps): React.JSX.Element => {
  const intl = useIntl();
  const { theme } = useAppContext();
  const safeRows = Array.isArray(rows) ? rows : [];

  const resolveTitle = (title: TranslatableLabel): string =>
    typeof title === 'string' ? title : intl.formatMessage({ id: title.id }, title.values);
  return (
    <Grid
      data-testid='overview-table'
      container
      direction='column'
      justifyContent='space-between'
    >
      <Grid
        container
        justifyContent='space-between'
        pb={1}
      >
        <Grid item>
          <IntroHeader
            header={header}
            subHeader={subHeader}
            theme={theme}
          />
        </Grid>
        <Grid item>
          <Grid
            container
            gap={1}
          >
            {extraActions.map(action => (
              <Grid
                item
                key={action.label}
              >
                <Tooltip
                  title={
                    action.disabled && action.tooltip
                      ? intl.formatMessage({ id: action.tooltip })
                      : ''
                  }
                >
                  <Button
                    {...(action.icon && { icon: action.icon })}
                    {...(action.rightIcon && { rightIcon: action.rightIcon })}
                    {...(action.leftIcon && { leftIcon: action.leftIcon })}
                    disabled={action.disabled ?? false}
                    onClick={action.onClick}
                    label={action.label}
                    variant={getTheme(theme)}
                  />
                </Tooltip>
              </Grid>
            ))}
            {editAction && (
              <Grid item>
                <Tooltip
                  title={
                    editAction.disabled && editAction.tooltip
                      ? intl.formatMessage({ id: editAction.tooltip })
                      : ''
                  }
                  alignment='end'
                >
                  <Button
                    disabled={editAction.disabled ?? false}
                    data-testid='edit-button'
                    onClick={editAction.onClick}
                    label={intl.formatMessage({ id: 'edit' })}
                  />
                </Tooltip>
              </Grid>
            )}
            {deleteAction && (
              <Grid item>
                <Tooltip
                  title={
                    deleteAction.disabled && deleteAction.tooltip
                      ? intl.formatMessage({ id: deleteAction.tooltip })
                      : ''
                  }
                  alignment='end'
                >
                  <Button
                    variant='outline'
                    disabled={deleteAction.disabled ?? false}
                    onClick={deleteAction.onClick}
                    label={intl.formatMessage({ id: 'delete' })}
                  />
                </Tooltip>
              </Grid>
            )}
            {trailingActionSlot ? <Grid item>{trailingActionSlot}</Grid> : null}
          </Grid>
        </Grid>
      </Grid>

      <Grid item>
        <TableContainer>
          <Table>
            <TableBody>
              {safeRows.map(row => (
                <TableRow key={typeof row.title === 'string' ? row.title : row.title.id}>
                  <TableCell>
                    <b>{resolveTitle(row.title)}</b>
                  </TableCell>
                  <TableCell align='right'>
                    {row.error ? (
                      <Grid
                        item
                        sx={{ display: 'inline-block', maxWidth: 'fit-content' }}
                      >
                        <Alert
                          mode='error'
                          title={intl.formatMessage({
                            id: typeof row.error === 'string' ? row.error : row.error.message,
                          })}
                          text=''
                        />
                      </Grid>
                    ) : (
                      (row.component ?? (
                        <ODSText
                          className='p-text'
                          style={{
                            margin: 0,
                          }}
                          as='p'
                        >
                          {row.value ?? intl.formatMessage({ id: 'notAvailable' })}
                        </ODSText>
                      ))
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default OverviewTable;
